import React from "react";
import LegalLayout, { Section, List } from "./LegalLayout";
import { EMAIL, ADDRESS } from "@/lib/siteData";

const RETENTION = [
  ["Demande de devis / contact sans suite", "3 ans à compter du dernier contact"],
  ["Preuve de consentement (dont adresse IP et navigateur)", "3 ans à compter de la fin du consentement (expiration ou retrait)"],
  ["Consentement au démarchage", "valable 12 mois maximum, sans renouvellement tacite"],
  ["Dossier client (contrat conclu)", "durée de la relation + 5 ans (obligations légales, LCB-FT)"],
  ["Conversations avec l'assistant IA", "3 ans maximum"],
  ["Choix de cookies / cookies publicitaires", "6 mois / 13 mois maximum"],
];

const PROCESSORS = [
  ["Hostinger International Ltd", "Hébergement du site", "Chypre (Union européenne)"],
  ["Resend", "Envoi des emails de confirmation et de notification", "États-Unis"],
  ["OpenAI", "Génération des réponses de l'assistant IA", "États-Unis"],
  ["Calendly", "Prise de rendez-vous (lien externe)", "États-Unis"],
  ["Oggo Data", "Comparateurs d'assurance en ligne (données saisies dans les formulaires de comparaison)", "France"],
  ["Google Ireland / Google LLC (Google Ads)", "Mesure des demandes issues des publicités, reciblage — uniquement avec votre accord", "Irlande, États-Unis"],
  ["Meta Platforms Ireland (pixel Meta)", "Mesure des publicités Facebook/Instagram, reciblage — uniquement avec votre accord", "Irlande, États-Unis"],
];

export default function Confidentialite() {
  return (
    <LegalLayout eyebrow="RGPD" title="Politique de confidentialité" testId="privacy-page">
      <Section n="1" title="Responsable du traitement">
        <p>MD Courtage — Maël DUBOIS, entrepreneur individuel — {ADDRESS} — {EMAIL}. Pour toute question relative à vos données, écrivez à cette adresse.</p>
      </Section>

      <Section n="2" title="Données collectées">
        <List items={[
          "Identité et coordonnées : nom, email, téléphone (facultatif si vous ne souhaitez pas être rappelé).",
          "Informations sur votre projet : besoins, profil, montant, échéance, message.",
          "Preuve de consentement : date et heure, texte accepté, version, adresse IP, navigateur, date d'expiration et de retrait.",
          "Messages échangés avec l'assistant IA (ne saisissez pas de données de santé ni bancaires).",
        ]} />
      </Section>

      <Section n="3" title="Finalités et bases légales">
        <List items={[
          "Répondre à votre demande de devis ou de contact par email — mesures précontractuelles prises à votre demande.",
          "Vous rappeler par téléphone au sujet de votre demande — votre consentement (case dédiée, facultative).",
          "Vous adresser des offres commerciales par téléphone et/ou par email — votre consentement, recueilli séparément pour chaque canal.",
          "Conserver la preuve de votre consentement, y compris l'adresse IP et le navigateur — obligation légale (article L.223-1 du Code de la consommation) et intérêt légitime à se défendre en cas de litige.",
          "Exécuter les missions de courtage et respecter nos obligations réglementaires (devoir de conseil, LCB-FT) — contrat et obligation légale.",
          "Faire fonctionner l'assistant IA — intérêt légitime à répondre à vos questions générales.",
          "Vous inviter, une seule fois après la signature d'un contrat, à laisser un avis sur Google — intérêt légitime ; vous pouvez vous y opposer à tout moment.",
        ]} />
      </Section>

      <Section n="4" title="Destinataires et sous-traitants">
        <p>
          Vos données sont destinées à MD Courtage. Elles ne sont jamais vendues ni louées. Elles ne sont transmises aux
          banques, assureurs, sociétés de gestion ou mandants (INOVEA, RB COURTAGE) que pour l'étude de votre dossier et avec
          votre accord. Nos sous-traitants n'y accèdent que pour les besoins du service :
        </p>
        <div className="bg-white border border-md-border rounded-2xl divide-y divide-md-border" data-testid="privacy-processors-table">
          {PROCESSORS.map(([n, r, c]) => (
            <div key={n} className="grid sm:grid-cols-3 gap-1 px-5 py-3.5 text-sm">
              <span className="font-medium text-md-primary">{n}</span>
              <span className="text-md-muted">{r}</span>
              <span className="text-md-muted">{c}</span>
            </div>
          ))}
        </div>
        <p>
          Les transferts vers les États-Unis sont encadrés par le Data Privacy Framework UE–États-Unis lorsque le prestataire y
          est certifié, ou à défaut par les clauses contractuelles types de la Commission européenne. Une copie de ces
          garanties peut être obtenue sur demande.
        </p>
      </Section>

      <Section n="5" title="Durées de conservation">
        <div className="bg-white border border-md-border rounded-2xl divide-y divide-md-border" data-testid="privacy-retention-table">
          {RETENTION.map(([k, v]) => (
            <div key={k} className="grid sm:grid-cols-2 gap-1 px-5 py-3.5 text-sm">
              <span className="font-medium text-md-primary">{k}</span>
              <span className="text-md-muted">{v}</span>
            </div>
          ))}
        </div>
        <p>Une suppression automatique est effectuée chaque semaine à l'issue de ces durées.</p>
      </Section>

      <Section n="6" title="Cookies et traceurs">
        <p>
          Sans votre accord, seuls des éléments techniques strictement nécessaires sont stockés sur votre appareil (identifiant
          de session de l'assistant IA, mémorisation de vos choix). Avec votre accord uniquement, des cookies Google Ads et Meta
          mesurent les demandes issues de nos publicités et permettent de vous présenter des annonces (base légale : votre
          consentement, article 82 de la loi Informatique et Libertés). Votre choix est conservé 6 mois ; les cookies déposés
          ont une durée maximale de 13 mois. Vous pouvez le modifier à tout moment via « Gérer les cookies » en bas de page.
          Nous enregistrons aussi l'origine de votre demande (campagne publicitaire, page d'arrivée) avec votre formulaire, sans
          cookie, pour mesurer l'efficacité de nos annonces.
        </p>
      </Section>

      <Section n="7" title="Vos droits">
        <p>
          Vous disposez d'un droit d'accès, de rectification, d'effacement, de limitation, d'opposition (notamment à la
          prospection, à tout moment et sans motif), de portabilité, du droit de retirer votre consentement et de définir des
          directives post-mortem. Exercez-les par email à {EMAIL}, ou retirez directement votre consentement sur{" "}
          <a href="/consentement" className="underline text-md-primary">mdcourtage.fr/consentement</a>. Vous pouvez introduire
          une réclamation auprès de la CNIL (www.cnil.fr).
        </p>
      </Section>

      <Section n="8" title="Sécurité">
        <p>Les données sont transmises de manière chiffrée (HTTPS) et accessibles uniquement par le courtier via un espace protégé par mot de passe.</p>
      </Section>
    </LegalLayout>
  );
}
